const collectionTypeTags = {
  share_flat: "Co-Living",
  rent: "Rent",
  sell: "Sale",
  residential_property: "Residential",
  commercial_property: "Commercial",
  residential: "Residential",
  commercial: "Commercial",
  PG: "PG",
  plot: "Plot",
  all: "All",
  users: "Profile",
};

const collectionTypeIcons = {
  share_flat: "👥",
  rent: "💸",
  sell: "🏷️",
  residential_property: "🏠",
  commercial_property: "🏢",
  residential: "🏠",
  commercial: "🏢",
  PG: "🛏️",
  plot: "🗺️",
};

const propertyTypeTags = {
  residential_property: "Residential Property",
  commercial_property: "Commercial Property",
  "flat/apartment": "Flat",
  floor: "Individual Floor",
  independent_house: "House",
  "vila/farm_house": "Villa",
  "1_rk_studio": "1RK/Studio House",
  others: "Others",
  office: "Office Space",
  warehouse: "Warehouse",
  retail_shop: "Retail Shop",
  showroom: "Showroom",
  other: "Other",
  plot: "Plot/Land",
  need_flat: "Shared Flat",
  need_flatmate: "Flatmate",
  pg_hostel: "PG",
  ready_to_use_office_space: "Ready to use office space",
  ready_to_use: "Ready to use office space",
  bareshell_office_space: "Bareshell office space",
  bare_shell: "Bareshell office space",
  pg: "PG",
};

export const formatIndianNumber = (num) => {
  const amount = +num;
  return amount?.toLocaleString("en-IN") ?? 0;
};

export const formatPricing = (num) => {
  if (!num) return 0;
  const price = Number(num);

  if (isNaN(price)) return "Invalid Number";

  const units = [
    { value: 1e7, suffix: "Crore" },
    { value: 1e5, suffix: "Lakhs" },
    { value: 1e3, suffix: "Thousand" },
  ];

  for (const { value, suffix } of units) {
    if (price >= value) {
      const formattedNumber = +(price / value).toFixed(
        price % value === 0 ? 0 : 2
      );
      return { price: formattedNumber, suffix };
    }
  }

  return { price: price, suffix: "" };
};

const getPricing = (data) => {
  if (data?.collection_type === "PG") {
    if (data?.pricing && Object.entries(data?.pricing).length > 0) {
      const lowest = Object.entries(data.pricing).reduce(
        (min, [key, value]) => {
          return value?.price < min.value.price ? { key, value } : min;
        },
        { key: "", value: { price: Infinity } }
      );

      return {
        actualPrice: { price: `₹ ${formatIndianNumber(lowest.value.price)}` },
      };
    }
    return;
  }

  const price = data?.approx_price;
  let formattedPrice;
  if (!price) {
    return;
  }

  if (String(Math.abs(price)).length > 5) {
    const actualPricing = formatPricing(price) || {};
    formattedPrice = {
      price: `₹ ${actualPricing.price} ${actualPricing.suffix}`,
    };
  } else {
    formattedPrice = { price: `₹ ${formatIndianNumber(price)}` };
  }
  if (!data?.is_hot_deal) {
    return { actualPrice: formattedPrice };
  }
  const discountPercentage =
    data?.discount?.type === "percentage"
      ? data.discount.amount
      : (((data?.discount?.amount || 0) / price) * 100).toFixed(2);

  const formattedDiscountAmount = (
    data?.discount?.type === "percentage"
      ? (price * (data.discount.amount || 0)) / 100
      : data?.discount?.amount || 0
  ).toFixed();

  const discountAmount = price - formattedDiscountAmount;
  let discountedPrice;

  if (String(Math.abs(discountAmount)).length > 5) {
    discountedPrice = `₹ ${getPricing(discountAmount)}`;
  } else {
    discountedPrice = `₹ ${formatIndianNumber(discountAmount)}`;
  }

  return {
    actualPrice: formattedPrice,
    discountAmount: discountAmount,
    discountedPrice: discountedPrice,
    discountPercentage,
  };
};

const ownershipTags = {
  freehold: "Freehold",
  power_of_attorney: "Power of attorney",
  leasehold: "Lease Holder",
  cooperative_society: "Cooperative Society",
};

const hashtagsOptions = "#HexaHome #RealEstate #property";

const adTypeHashTags = {
  rent: "#rent #forrent #propertiesforrent",
  sell: "#salesalesale #forsale #propertyforsale",
  PG: "#PG #sharedliving",
  plot: "#salesalesale #plot #plotsforsale #plot",
  share_flat: "#coliving #sharedflat #flatsharing #roommates"
};
const propertyTypeHashtags = {
  residential_property: "#residential #home #realestate",
  commercial_property: "#Commercial #businessproperty #realestate",
  "flat/apartment": "#flats #appartment",
  floor: "#IndividualFloor #floors #homelayout",
  independent_house: "#house #realestate",
  "vila/farm_house": "#villa #villas #farmhouse #LuxuryLiving",
  "1_rk_studio": "#studioapartment #1RK #CompactLiving",
  office: "#officespace #CommercialProperty",
  warehouse: "#warehouse #storagespace #CommercialProperty",
  retail_shop: "#RetailShop #storefront #CommercialSpace",
  showroom: "#showroom #commercialdisplay #PropertyListing",
  plot: "#landforsale #plots #plotsforsale #InvestInLand",
  need_flat: "#roomsharing",
  need_flatmate: "#roommatesearch",
  pg_hostel: "#PG #hostellife #studenthousing",
  ready_to_use_office_space: "#furnishedoffice #ReadyOffice #office",
  bareshell_office_space: "#BareshellOffice #officesetup #office #CommercialProperty",
  bare_shell: "#bareshell #UnfurnishedOffice #Commercial",
};

const bhkHashtags = {
  "1BHK": "#1BHK",
  "2BHK": "#2BHK",
  "3BHK": "#3BHK",
  "4BHK": "#4BHK",
  "5BHK": "#5BHK"
};

const CONSTRUCTION_STATUS = {
  ready_to_move: "Ready to Move",
  under_construction: "Under Construction",
};

const FurnishTypeChipText = {
  unfurnished: "Unfurnished",
  semi_furnished: "Semi Furnished",
  fully_furnished: "Fully Furnished",
};

const AGE_OF_PROPERTY_LABELS = {
  new: "New",
  less_than_1_year: "Less than a Year old",
  "1_to_3_years": "1 to 3 Years old",
  "3_to_5_years": "3 to 5 Years old",
  "5_to_10_years": "5 to 10 Years old",
  more_than_10_years: "More than 10 Years old",
};

const directionEnums = {
  north: "North",
  south: "South",
  east: "East",
  west: "West",
  "north-east": "North East",
  "north-west": "North West",
  "south-east": "South East",
  "south-west": "South West",
};
const MEALS_AVAILABILITY_LABELS = {
  yes: "🍽️ Meals Included",
  need_to_pay_extra: "🍽️ Extra Cost for Meals",
};


const formatPropertyData = {
  age_of_property: (value) => `🏗️ ${AGE_OF_PROPERTY_LABELS[value]}`,
  balconies: (value) =>
    `🛋️ ${value} ${Number(value) === 1 ? "Balcony" : "Balconies"}`,
  ownership: (value) => `📜 ${ownershipTags[value]}`,
  construction_status: (value)=> `🚧 ${CONSTRUCTION_STATUS[value]}`,
  furnish_type: (value)=> `🛋️ ${FurnishTypeChipText[value]}`,
  area: (value)=> `🌐 ${value}`,
  bhk: (value)=> `🏠 ${value}`,
  facing: (value)=> `🧭 ${directionEnums[value]}`,
  bathrooms: (value)=> `🛋️ ${value} ${Number(value) === 1 ? "Bathroom" : "Bathrooms"}`,
  private_room: () => `🛏️ Private Room`,
  twin_sharing: () => `🛌 Twin Sharing`,
  triple_sharing: () => `🛌 Triple Sharing`,
  total_floors: (value) => `🏢 ${value} Total Floor${Number(value) > 1 ? "s" : ""}`,
  floors: (value) => `🏢 ${value} ${Number(value) > 1 ? "floors" : "floor"}`,
  quad_sharing: () => `🛌 Quad Sharing`,
  cctv: () => `🎥 CCTV Surveillance`,
  lift: () => `🛗 Lift Facility`,
  storage_sheds: () => `🏚️ Storage Sheds`,
  meals_available: (value) => MEALS_AVAILABILITY_LABELS[value] || ''
};

const getHightLightsArray = (data) => {
  return Object.entries(data)
    .map(([key, value]) => {
      const formatter = formatPropertyData[key];
      return formatter ? formatter(value) : value;
    })
    .join(" | ");
};

const getOfficeTags = (features = {}) => {
  const featureMap = {
    conference_room: (val) => {
      if (!val) return '';
      return `📊 ${val} Conference Room${parseInt(val) > 1 ? 's' : ''}`;
    },
    furnishing: (val) => val === 'true' ? '🛋️ Furnished Office' : '',
    reception_area: (val) => val === 'true' ? '🛎️ Reception Area' : '',
    pantry: (val) => {
      if (!val) return '';
      return `🥪 ${val === 'private' ? 'Private Pantry' : 'Common Pantry'}`;
    },
    central_ac: (val) => val === 'true' ? '❄️ Central AC' : '',
    ups: (val) => val === 'true' ? '🔌 Power Backup' : '',
    parking_type: (val) => val === 'available' ? '🅿️ Parking Available' : '',
  };

  return Object.entries(featureMap)
    .map(([key, renderFn]) => {
      try {
        const value = features?.[key];
        return renderFn?.(value, features) || '';
      } catch {
        return '';
      }
    })
    .filter(Boolean)
    .join(' | ');
};


export const getPostMessage = (data) => {
  const {
    property_type,
    collection_type,
    ad_type,
    location,
    mainFeatures,
    highlights,
    sharing_type,
    office_features,
    owner
  } = data || {};
  const { bhk } = mainFeatures || {};
  const { locality, city } = location || {};
  const pricing = getPricing(data);

  const adTypeMsg =
    collection_type === "plot"
      ? "Sale"
      : collection_type === "PG"
      ? "Rent"
      : collectionTypeTags[ad_type] || "";

  const propertyLabel = propertyTypeTags[property_type] || "";
  const collectionLabel =
    collection_type === "plot" || collection_type === "PG"
      ? collectionTypeTags[collection_type]
      : "";

  let pricingSubText = "";
  if (ad_type === "sell") {
    pricingSubText = " only!";
  } else if (ad_type === "rent") {
    pricingSubText = "/Month";
  } else if (collection_type === "plot") {
    pricingSubText = " only!";
  } else if (collection_type === "PG") {
    pricingSubText = "/Month";
  }

  let message = `${collectionTypeIcons[collection_type]}`;
  if(sharing_type ==='need_flat'){
    const {first_name, last_name} = owner || {}
    let msg=` ${first_name} ${last_name} is looking for a room in`
    if (locality || city) {
      msg += ` in `;
      if (locality) msg += `${locality}, `;
      msg += city;
    }
    message += msg
  }else if(sharing_type ==='need_flatmate'){
    const {first_name, last_name} = owner || {}
    let msg=` ${first_name} ${last_name} is sharing a room in ${data?.bhk ?? 'his'} Flat`
    if (locality || city) {
      msg += ` in `;
      if (locality) msg += `${locality}, `;
      msg += city;
    }
    message += msg
  }else{
    if (bhk) message += `${bhk} `;
    if (propertyLabel) message += `${propertyLabel} `;
    if (collectionLabel) message += `${collectionLabel} `;
    message += `for ${adTypeMsg}`;
    if (locality || city) {
      message += ` in `;
      if (locality) message += `${locality}, `;
      message += city;
    }
    if (pricing?.actualPrice?.price) {
      message += ` – ${pricing.actualPrice.price}${pricingSubText}`;
    }
  }

  const highlightsOptions = getHightLightsArray({ ...highlights, ...mainFeatures });
  const officeOptions=getOfficeTags(office_features) || ''

  const hashtags = `${hashtagsOptions} ${
    adTypeHashTags[ad_type || collection_type] || ""
  } ${
    propertyTypeHashtags[property_type || sharing_type] || ""
  } ${
    bhkHashtags[bhk || data.bhk] || ""
  }${
    bhkHashtags[bhk] && (ad_type==='rent' || ad_type==='sell') ? ` ${bhkHashtags[bhk]}For${collectionTypeTags[ad_type] || ''}`  : ""
  } #${city}RealEstate #${city}`;

  return `${message}${highlightsOptions ? `\n${highlightsOptions}` : ""} ${officeOptions}\n${hashtags}`;
};
